<template>
  <div v-if="showBanner" class="consent-banner">
    <div class="consent-content">
      <div class="consent-text">
        <h3>Data Privacy & Consent</h3>
        <p>
          This survey collects and processes your personal data to provide you
          with cybersecurity competency insights. We use cookies and local
          storage to save your progress and provide a better experience.
        </p>
        <div class="data-retention-info">
          <h4>Data Retention Policy:</h4>
          <ul>
            <li>
              <strong>Survey Data:</strong> Stored for 2 years for research
              purposes
            </li>
            <li>
              <strong>Account Information:</strong> Retained until you request
              deletion
            </li>
            <li>
              <strong>Progress Data:</strong> Automatically cleared after survey
              completion
            </li>
          </ul>
        </div>
        <p>
          By continuing, you consent to our data processing practices. You can
          withdraw consent at any time.
          <a href="#" @click.prevent="showPrivacyPolicy">View Privacy Policy</a>
        </p>
      </div>
      <div class="consent-actions">
        <button @click="acceptConsent" class="btn-accept">
          Accept & Continue
        </button>
        <button @click="declineConsent" class="btn-decline">Decline</button>
        <router-link to="/survey-info" class="survey-info-link"
          >Learn more about this survey</router-link
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

const emit = defineEmits(["consent-given", "consent-declined"]);

const showBanner = ref(false);

const acceptConsent = () => {
  // Store consent in localStorage
  localStorage.setItem("surveyConsent", "true");
  localStorage.setItem("consentDate", new Date().toISOString());
  showBanner.value = false;
  emit("consent-given");
};

const declineConsent = () => {
  showBanner.value = false;
  emit("consent-declined");
};

const showPrivacyPolicy = () => {
  // You can implement this to show a modal or navigate to privacy policy
  alert(
    "Privacy Policy: Your data is used solely for cybersecurity research and competency assessment. We do not share your data with third parties."
  );
};

onMounted(() => {
  // Check if user has already given consent
  const hasConsent = localStorage.getItem("surveyConsent");
  if (!hasConsent) {
    showBanner.value = true;
  }
});
</script>

<style scoped>
.consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #dae2e9;
  color: #222;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.consent-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.consent-text {
  flex: 1;
}

.consent-text h3 {
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.consent-text p {
  margin: 0 0 15px 0;
  line-height: 1.5;
  font-size: 0.9rem;
}

.data-retention-info {
  background: #c3d0db;
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
}

.data-retention-info h4 {
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 600;
}

.data-retention-info ul {
  margin: 0;
  padding-left: 20px;
  font-size: 0.85rem;
}

.data-retention-info li {
  margin: 5px 0;
  line-height: 1.4;
}

.consent-text a {
  color: #005461;
  text-decoration: underline;
}

.consent-text a:hover {
  color: #003d49;
}

.consent-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 150px;
}

.btn-accept,
.btn-decline {
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.btn-accept {
  background: #005461;
  color: white;
}

.btn-accept:hover {
  background: #003d49;
  transform: translateY(-1px);
}

.btn-decline {
  background: transparent;
  color: #005461;
  border: 2px solid #005461;
}

.btn-decline:hover {
  background: rgba(0, 84, 97, 0.08);
  border-color: #003d49;
}

.survey-info-link {
  display: inline-block;
  margin-top: 0.75rem;
  color: #005461;
  font-weight: 600;
  text-decoration: underline;
  font-size: 1rem;
  transition: color 0.2s;
}
.survey-info-link:hover {
  color: #003d49;
}

@media (max-width: 768px) {
  .consent-content {
    flex-direction: column;
    gap: 15px;
  }

  .consent-actions {
    flex-direction: row;
    justify-content: center;
  }

  .btn-accept,
  .btn-decline {
    flex: 1;
    max-width: 150px;
  }
}
</style>
